import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {tap} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {HeaderFilter} from 'asax-common';
import {SkyObjectsService} from "../sky-objects.service";
import {TextSearchInput} from "../../search-input.model";

export interface DropdownSkyObjectItem {
  value: string;
  text: string;
  selected?: boolean;
  resolverType?: string;
}

const separator = '$';

@Component({
  selector: 'asax-header-search-input-sky-objects',
  templateUrl: './search-input-sky-objects.component.html',
  styleUrls: ['./search-input-sky-objects.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputSkyObjectsComponent implements OnInit, OnChanges {
  @Input() searchInput: TextSearchInput;

  @Output() outFilter = new EventEmitter<HeaderFilter>();

  valueParsed: any;

  isBestMatchSelected: boolean;

  skyObjectsFilterValue: string;
  skyObjectsDropdownItems: DropdownSkyObjectItem[];

  private valueToDisplay: string;
  private valueSubject = new Subject<string>();

  constructor(private readonly skyObjectsService: SkyObjectsService) {
  }

  ngOnInit(): void {
    this.skyObjectsService.fetchSkyObjects().pipe(
      tap(skyObjectsDropdownItems => this.skyObjectsDropdownItems = skyObjectsDropdownItems),
      tap(() => this.selectDropdownItemsFromValue()),
      tap(() => this.valueChanged())
    ).subscribe();

    this.valueSubject.asObservable().pipe(
      tap(() => this.buildValue()),
      tap(() => this.buildValueToDisplay()),
      tap(() => this.buildValueParsed()),
      tap(() => this.emitFilter())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.skyObjectsDropdownItems?.length > 0) {
      this.selectDropdownItemsFromValue();
      this.valueChanged();
    }
  }

  valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  onSkyObjectClick(skyObject: DropdownSkyObjectItem) {
    skyObject.selected = !skyObject.selected;
    this.valueChanged();
  }

  clearSearchInputValue(): void {
    this.skyObjectsDropdownItems?.forEach((dropdownItem: DropdownSkyObjectItem) => dropdownItem.selected = false);
    this.valueChanged();
  }

  clearSkyObjectsFilterValue(): void {
    this.skyObjectsFilterValue = '';
  }

  shouldSkyObjectBeDisplayed(skyObjects: DropdownSkyObjectItem): boolean {
    return this.skyObjectsFilterValue === undefined || skyObjects?.text?.toLowerCase().includes(this.skyObjectsFilterValue?.toLowerCase());
  }

  toggleBestMatch(): void {
    this.isBestMatchSelected = !this.isBestMatchSelected;
    this.valueChanged();
  }

  private buildValue(): void {
    this.searchInput.value = this.skyObjectsDropdownItems?.filter(({selected}) => !!selected)
      .map(({value, resolverType}) => `${value}${separator}${resolverType}`)
      .join(',');
  }

  private buildValueToDisplay(): void {
    const numberOfSkyObjectsSelected = this.skyObjectsDropdownItems?.filter(({selected}) => !!selected)
      .length;

    if (numberOfSkyObjectsSelected > 0) {
      this.valueToDisplay = `${numberOfSkyObjectsSelected} selected`;
    } else {
      this.valueToDisplay = '';
    }
  }

  private buildValueParsed() {
    if (this.searchInput.value?.length > 0) {
      const skyObjectSimbadTypes = this.skyObjectsDropdownItems?.filter(i => i.resolverType === 'SIMBAD').filter(i => i.selected).map(i => i.value).join(',');
      const skyObjectNedTypes = this.skyObjectsDropdownItems?.filter(i => i.resolverType === 'NED').filter(i => i.selected).map(i => i.value).join(',')
      const sBestMatchSelected = this.isBestMatchSelected;

      this.valueParsed = {sBestMatchSelected, skyObjectSimbadTypes, skyObjectNedTypes};
    } else {
      this.valueParsed = undefined;
    }
  }

  private selectDropdownItemsFromValue(): void {
    const dropdownItemsSelected = this.searchInput?.value?.split(',').map(token => token.trim()).map(token => token?.split(separator));
    this.skyObjectsDropdownItems?.forEach((dropdownItem: DropdownSkyObjectItem) => {
      const isDropdownItemsSelected = dropdownItemsSelected?.some(([value, resolverType]) => dropdownItem.value === value && dropdownItem.resolverType === resolverType);
      if (isDropdownItemsSelected) {
        dropdownItem.selected = true;
      } else {
        dropdownItem.selected = false;
      }
    });
  }

  private emitFilter(): void {
    const filter = {
      id: this.searchInput.id,
      value: this.searchInput.value,
      valueParsed: this.valueParsed,
      valueToDisplay: this.valueToDisplay,
      label: this.searchInput.label,
      isDisabled: this.searchInput.isDisabled,
      elasticParserType: this.searchInput.elasticParserType
    };

    this.outFilter.emit(filter);
  }
}
